/**
 * Mystery-bouquet colour schemes — the single, central definition of the
 * colour-scheme options a customer picks on a mystery bouquet.
 *
 * A mystery bouquet lets the customer choose ONLY a colour scheme (handled as
 * a product variant); the florist designs the rest. The same handful of
 * schemes ("Soft pastels", "Bold & bright", …) is reused across every mystery
 * product, so they're defined ONCE in Settings → Storefront content instead of
 * re-typed under each product. The Products page then applies/syncs them onto
 * the individual products' variant rows (cart/checkout/feed/orders/florist all
 * keep working against real per-product variants — see [[project_variant_selection_capture]]).
 */

import type { VariantInput } from "./product-variants";

export interface MysteryColourScheme {
  /** Customer-facing label, e.g. "Soft pastels". */
  label: string;
  /**
   * Optional absolute price for this scheme; `null`/omitted = inherit the
   * product's base price (the usual case — the colour choice doesn't change
   * the price). Kept so a scheme could carry an upcharge if ever needed.
   */
  pricePence?: number | null;
}

/**
 * The canonical colour schemes. These mirror the set every live mystery
 * bouquet already uses (verified against prod D1, 2026-07-17 — all six mystery
 * products carried this identical list), so it's the default: the central
 * config reflects reality out of the box, a fresh mystery product gets the
 * right options, and a bulk sync is a no-op against the current catalogue.
 * (Mirrors how colours / carriers / services default in `products-settings.ts`.)
 */
export const DEFAULT_MYSTERY_COLOUR_SCHEMES: MysteryColourScheme[] = [
  { label: "Blush Pink", pricePence: null },
  { label: "Sky Blue", pricePence: null },
  { label: "Romantic Purple", pricePence: null },
  { label: "Pure White", pricePence: null },
  { label: "Forest Green", pricePence: null },
  { label: "Sunny Yellow", pricePence: null },
  { label: "Dark & Moody", pricePence: null },
  { label: "Colour Symphony", pricePence: null },
];

const MAX_SCHEMES = 20;
const MAX_LABEL_LEN = 100;
const MAX_PRICE_PENCE = 99999999;

/**
 * Validate + normalise a raw mystery-colour-schemes array from stored JSON or
 * an admin request body. Drops blank-label rows, dedupes case-insensitively,
 * caps count + label length, clamps prices to a non-negative integer pence (or
 * null = inherit). Returns the default set when the input is absent/invalid
 * (non-array); an explicit empty array IS respected (admin cleared the list).
 */
export function normalizeMysteryColourSchemes(
  raw: unknown
): MysteryColourScheme[] {
  if (!Array.isArray(raw)) return DEFAULT_MYSTERY_COLOUR_SCHEMES;
  const out: MysteryColourScheme[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const label = typeof r.label === "string" ? r.label.trim() : "";
    if (!label || label.length > MAX_LABEL_LEN) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    let pricePence: number | null = null;
    if (r.pricePence != null && r.pricePence !== "") {
      const n = Number(r.pricePence);
      if (Number.isFinite(n) && n >= 0 && n <= MAX_PRICE_PENCE) {
        pricePence = Math.round(n);
      }
    }
    seen.add(key);
    out.push({ label, pricePence });
    if (out.length >= MAX_SCHEMES) break;
  }
  return out;
}

/**
 * Convert the central schemes to the `VariantInput[]` shape the product-save
 * path expects. The inputs carry NO `id`, so `syncProductVariants` creates
 * fresh rows and deletes any existing ones — a wholesale replace, which is the
 * intent when applying/syncing the shared colour schemes onto a product.
 */
export function mysteryColourSchemesToVariantInputs(
  schemes: MysteryColourScheme[]
): VariantInput[] {
  const out: VariantInput[] = [];
  for (const s of schemes) {
    const label = s.label.trim();
    if (!label) continue;
    out.push({ label, pricePence: s.pricePence ?? null, stock: -1 });
  }
  return out;
}

/**
 * True when a product title marks it as a mystery bouquet. Matches how the
 * /mystery-bouquets page selects products (`listProducts q:"mystery"`, which
 * matches the title only) — the shared rule for the bulk "sync to all mystery
 * bouquets" action.
 */
export function isMysteryBouquetTitle(
  title: string | null | undefined
): boolean {
  return typeof title === "string" && title.toLowerCase().includes("mystery");
}

/**
 * True for a FRESH mystery bouquet — the products that share the mystery size
 * ladder (Simple → Luxury) and so get the "Size guide" reference. Crochet /
 * preserved mystery pieces are excluded (they aren't part of that fresh ladder).
 */
export function isMysteryFreshProduct(
  category: string | null | undefined,
  title: string | null | undefined
): boolean {
  return (
    isMysteryBouquetTitle(title) &&
    typeof category === "string" &&
    category.trim().toLowerCase() === "fresh"
  );
}

/* ─────────────────── Mystery size guide (admin-configurable) ─────────────
 * The "Size guide" modal on mystery-fresh products shows the tier ladder
 * (Simple → Luxury). Price / image / name / order come LIVE from the actual
 * products (so they update whenever a product does); only the editorial layer
 * is stored here — an on/off switch, an intro line, and per-tier size label +
 * "best for" text, keyed by product id. Entries with blank fields fall back to
 * the built-in defaults below, so it reads well out of the box and is fully
 * overridable in admin.
 */

export interface MysterySizeGuideEntry {
  /** The mystery-fresh product this annotates (matched to a live product). */
  productId: string;
  /** Short scale label, e.g. "Petite", "Grand". Blank → built-in default. */
  sizeLabel?: string;
  /** Who/what it suits, e.g. "Birthdays & surprises". Blank → built-in default. */
  bestFor?: string;
}

export interface MysterySizeGuideConfig {
  /** Show the "Size guide" button on mystery-fresh products. Default true. */
  enabled: boolean;
  /** Intro line shown at the top of the modal. */
  intro: string;
  /** Illustrated size-reference image (path or URL). Blank → no image, ladder only. */
  imageUrl?: string;
  /** Per-tier editorial annotations (matched to live products by id). */
  entries: MysterySizeGuideEntry[];
}

export const DEFAULT_MYSTERY_SIZE_GUIDE_INTRO =
  "Every mystery bouquet is the same designer's-choice surprise — the tiers below simply differ in size and fullness. Pick the scale that suits your occasion.";

/** Illustrated size chart shipped in `public/asset` (XS Simple → XXL Luxury). */
export const DEFAULT_MYSTERY_SIZE_GUIDE_IMAGE = "/asset/mystery-size-guidance.png";

export const DEFAULT_MYSTERY_SIZE_GUIDE: MysterySizeGuideConfig = {
  enabled: true,
  intro: DEFAULT_MYSTERY_SIZE_GUIDE_INTRO,
  imageUrl: DEFAULT_MYSTERY_SIZE_GUIDE_IMAGE,
  entries: [],
};

/**
 * Built-in fallback labels, keyed by a title substring (products are stable;
 * an unmatched tier still renders name/price/image/scale, just without a
 * label). Size codes match the illustrated chart (XS Simple → XXL Luxury);
 * "best for" is drawn from each product's own copy — NOT invented measurements.
 * Used as display fallback + admin input placeholders.
 */
export const DEFAULT_MYSTERY_SIZE_LABELS: {
  match: string;
  sizeLabel: string;
  bestFor: string;
}[] = [
  { match: "simple", sizeLabel: "XS", bestFor: "Everyday & small gestures" },
  { match: "daily bloom", sizeLabel: "S", bestFor: "Casual, friendly gifting" },
  { match: "classic", sizeLabel: "M", bestFor: "Birthdays & thoughtful surprises" },
  { match: "elegant", sizeLabel: "L", bestFor: "Anniversaries & celebrations" },
  { match: "signature", sizeLabel: "XL", bestFor: "Big moments, generous volume" },
  { match: "luxury", sizeLabel: "XXL", bestFor: "Grand celebrations, premium blooms" },
];

/** Built-in size label/best-for for a product title (null when unmatched). */
export function defaultSizeMetaForTitle(
  title: string | null | undefined
): { sizeLabel: string; bestFor: string } | null {
  if (typeof title !== "string") return null;
  const t = title.toLowerCase();
  return DEFAULT_MYSTERY_SIZE_LABELS.find((m) => t.includes(m.match)) ?? null;
}

const MAX_SIZE_GUIDE_ENTRIES = 30;
const MAX_INTRO_LEN = 400;
const MAX_SIZE_LABEL_LEN = 40;
const MAX_BEST_FOR_LEN = 120;

/**
 * Validate + normalise the mystery-size-guide config from stored JSON or an
 * admin request body. `enabled` defaults on (only an explicit `false` disables);
 * intro falls back to the default; entries are deduped by productId, trimmed,
 * capped, and blank string fields dropped to `undefined` (→ display fallback).
 */
export function normalizeMysterySizeGuide(raw: unknown): MysterySizeGuideConfig {
  if (!raw || typeof raw !== "object") return DEFAULT_MYSTERY_SIZE_GUIDE;
  const o = raw as Record<string, unknown>;
  const enabled = o.enabled !== false;
  const intro =
    typeof o.intro === "string" && o.intro.trim()
      ? o.intro.trim().slice(0, MAX_INTRO_LEN)
      : DEFAULT_MYSTERY_SIZE_GUIDE_INTRO;
  // imageUrl: absent (legacy config) → default shipped chart; explicit blank →
  // undefined (admin chose to hide it); non-empty string → that path/URL.
  let imageUrl: string | undefined;
  if (typeof o.imageUrl === "string") {
    const s = o.imageUrl.trim();
    imageUrl = s ? s.slice(0, 400) : undefined;
  } else {
    imageUrl = DEFAULT_MYSTERY_SIZE_GUIDE_IMAGE;
  }
  const entries: MysterySizeGuideEntry[] = [];
  if (Array.isArray(o.entries)) {
    const seen = new Set<string>();
    for (const item of o.entries) {
      if (!item || typeof item !== "object") continue;
      const r = item as Record<string, unknown>;
      const productId = typeof r.productId === "string" ? r.productId.trim() : "";
      if (!productId || seen.has(productId)) continue;
      const sizeLabel =
        typeof r.sizeLabel === "string" && r.sizeLabel.trim()
          ? r.sizeLabel.trim().slice(0, MAX_SIZE_LABEL_LEN)
          : undefined;
      const bestFor =
        typeof r.bestFor === "string" && r.bestFor.trim()
          ? r.bestFor.trim().slice(0, MAX_BEST_FOR_LEN)
          : undefined;
      seen.add(productId);
      entries.push({ productId, sizeLabel, bestFor });
      if (entries.length >= MAX_SIZE_GUIDE_ENTRIES) break;
    }
  }
  return { enabled, intro, imageUrl, entries };
}
